import { useRef, useState } from 'react'
import { useAtomValue } from 'jotai'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Analytics } from 'analytics/analytics.events'
import { useRequiredParams } from 'hooks/params.hook'
import { containerToMoveAtom } from '../Receipt.state'
import { useGoToMoveContainers } from '../move-containers/MoveContainers.navigator'
import { moveContainer } from 'client/receipt/receipt.client'
import { getLocationFromScan } from 'client/scanner/scanner.client'
import { toastSuccess, toastError } from 'utils/toast'
import { RECEIPT_LOCATION_TYPE } from 'utils/constants'
import warehouses from '@justomx/warehouses'
import { getCurrentWarehouse } from 'client/utilities.client-helper'

export const useScanLocationData = () => {
  const navigate = useNavigate()
  const { t } = useTranslation('global')
  const [loading, setLoading] = useState(false)
  const container = useAtomValue(containerToMoveAtom)
  const moveContainers = useGoToMoveContainers()
  const { orderId, receiptId, receiptType } = useRequiredParams(
    'orderId',
    'receiptId',
    'receiptType'
  )
  const loadingRef = useRef(false)

  const onScan = async (scanned: string) => {
    if (!container || loadingRef.current) {
      console.info('skipping move container due to current request')
      return
    }

    try {
      const currentWarehouseCode = getCurrentWarehouse()
      const currentWarehouse = await warehouses.get(currentWarehouseCode.toUpperCase())
      const scannedLocation = await getLocationFromScan(scanned)
      if (currentWarehouse?.type === 'cedis' && scannedLocation.totalContainers > 0) {
        toastError(t('receipt-scan-location.location-occupied', { location: scanned }))
        return
      }
    } catch (error) {
      console.error('Error checking location containers:', error)
      toastError(t('receipt-scan-location.location-check-error', { location: scanned }))
      return
    }

    loadingRef.current = true
    setLoading(true)
    const eventData = {
      orderId,
      receiptType,
      originContainerName: container?.containerName,
      destinationLocationName: scanned,
      destinationLocationType: RECEIPT_LOCATION_TYPE
    }

    try {
      Analytics.receipt.tryMoveContainer(eventData)
      await moveContainer(receiptId, container.containerName, scanned)
      Analytics.receipt.moveContainer(eventData)

      toastSuccess(t('receipt-scan-location.scanned'))
      moveContainers.go(receiptId, orderId)
    } catch (error) {
      Analytics.receipt.moveContainerError({
        ...eventData,
        error: error.toString()
      })
      console.error(error)
    } finally {
      loadingRef.current = false
      setLoading(false)
    }
  }

  return {
    container,
    loading,
    actions: {
      onScan,
      goBack: () => {
        navigate(-1)
      }
    }
  }
}
